import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { AuthContext } from '../App';
import { 
  ArrowLeft, ArrowRight, Volume2, CheckCircle2, XCircle,
  Zap, Star, Sparkles, RefreshCw, BookOpen, Mic, Play,
  MessageSquare, Target, Award, ChevronDown, ChevronUp, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fill Blank Exercise Component with collapsible hint
function FillBlankExerciseLesson({ exercise, showResult, selectedOption, isCorrect, handleAnswer }) {
  const [showHint, setShowHint] = useState(false);
  
  return (
    <div>
      <p className="text-lg sm:text-xl font-bold text-gray-800 mb-6 text-center leading-relaxed">
        {exercise.content?.sentence}
      </p>
      
      {/* Collapsible Hint */}
      {exercise.content?.hint && (
        <div className="mb-4">
          <button
            onClick={() => setShowHint(!showHint)}
            className="flex items-center justify-center gap-2 mx-auto text-sm text-amber-600 hover:text-amber-700 transition-colors"
          >
            <Lightbulb className="w-4 h-4" />
            <span>{showHint ? 'İpucunu Gizle' : 'İpucu Göster'}</span>
            {showHint ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showHint && (
            <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200 animate-in slide-in-from-top-2">
              <p className="text-sm text-amber-700 text-center">
                💡 {exercise.content.hint}
              </p>
            </div>
          )}
        </div>
      )}
      
      <div className="space-y-3">
        {exercise.content?.options?.map((option, idx) => (
          <button
            key={idx}
            onClick={() => handleAnswer(option, idx)}
            disabled={showResult}
            className={`w-full p-4 rounded-xl font-bold text-left transition-all border-3 break-words ${
              showResult && idx === exercise.correct_answer
                ? 'bg-green-100 border-green-500 text-green-700'
                : showResult && selectedOption === idx && !isCorrect
                  ? 'bg-red-100 border-red-500 text-red-700'
                  : selectedOption === idx
                    ? 'bg-sky-100 border-sky-500'
                    : 'bg-white border-gray-200 hover:border-sky-400 hover:bg-sky-50'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-gray-400 shrink-0">{String.fromCharCode(65 + idx)}</span>
              <span className="flex-1">{option}</span>
              {showResult && idx === exercise.correct_answer && (
                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
              )}
              {showResult && selectedOption === idx && !isCorrect && (
                <XCircle className="w-5 h-5 text-red-500 shrink-0" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const CabinCrewLesson = () => {
  const navigate = useNavigate();
  const { level, moduleId } = useParams();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [phase, setPhase] = useState('learn'); // learn, results

  useEffect(() => {
    if (user && level && moduleId) {
      loadLesson();
    }
  }, [user, level, moduleId]);

  const loadLesson = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/cabin-crew/lesson/${level}/${moduleId}`,
        {},
        { withCredentials: true }
      );
      setLesson(response.data);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('Error loading lesson:', error);
      toast.error('Ders yüklenemedi');
      navigate('/cabin-crew');
    } finally {
      setLoading(false);
    }
  };

  const playAudio = async (text) => {
    try {
      const response = await axios.post(
        `${API}/speaking/text-to-speech`,
        { text },
        { withCredentials: true, responseType: 'blob' }
      );
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audio.play();
    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  const handleAnswer = async (answer, optionIndex = null) => {
    const exercise = lesson.exercises[currentIndex];
    setSelectedOption(optionIndex);
    
    // Simple validation for fill_blank type
    if (exercise.type === 'fill_blank') {
      const correct = optionIndex === exercise.correct_answer;
      setIsCorrect(correct);
      setShowResult(true);
      
      if (correct) {
        setScore(prev => prev + 1);
        toast.success('Doğru! 🎉');
      } else {
        toast.error('Yanlış!');
      }
      
      setAnswers(prev => ({
        ...prev,
        [currentIndex]: { answer: optionIndex, correct }
      }));
      
      // Auto advance after delay
      setTimeout(() => {
        if (currentIndex < lesson.exercises.length - 1) {
          setCurrentIndex(prev => prev + 1);
          setShowResult(false);
          setSelectedOption(null);
          setIsCorrect(null);
        } else {
          completeLesson();
        }
      }, 1500);
    } else if (exercise.type === 'scenario') {
      // For scenario, check is_correct from the option itself
      const selectedOption = exercise.content?.options?.[optionIndex];
      const correct = selectedOption?.is_correct === true;
      setIsCorrect(correct);
      setShowResult(true);
      
      if (correct) {
        setScore(prev => prev + 1);
        toast.success('Doğru! 🎉');
      } else {
        toast.error('Yanlış!');
      }
      
      setAnswers(prev => ({
        ...prev,
        [currentIndex]: { answer: optionIndex, correct }
      }));
      
      // Auto advance after delay
      setTimeout(() => {
        if (currentIndex < lesson.exercises.length - 1) {
          setCurrentIndex(prev => prev + 1);
          setShowResult(false);
          setSelectedOption(null);
          setIsCorrect(null);
        } else {
          completeLesson();
        }
      }, 1500);
    } else {
      // For vocabulary, dialogue, pa_announcement, icao_spelling etc - just mark as seen and continue
      setAnswers(prev => ({
        ...prev,
        [currentIndex]: { answer: 'viewed', correct: true }
      }));
      setScore(prev => prev + 1);
      
      // Move to next exercise
      if (currentIndex < lesson.exercises.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        completeLesson();
      }
    }
  };

  const nextExercise = () => {
    if (currentIndex < lesson.exercises.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setShowResult(false);
      setSelectedOption(null);
      setIsCorrect(null);
    } else {
      completeLesson();
    }
  };

  const completeLesson = async () => {
    const finalScore = (score / lesson.exercises.length) * 100;
    setPhase('results');
    
    try {
      await axios.post(
        `${API}/cabin-crew/complete-lesson`,
        null,
        { 
          params: { session_id: sessionId, score: finalScore },
          withCredentials: true 
        }
      );
      toast.success(`Ders tamamlandı! ${Math.round(finalScore)}% skor`);
    } catch (error) {
      console.error('Complete error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-20 h-20 border-8 border-sky-200 border-t-sky-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-xl font-bold text-gray-700">Ders hazırlanıyor...</p>
        </div>
      </div>
    );
  }

  // Results Phase
  if (phase === 'results') {
    const finalScore = (score / lesson.exercises.length) * 100;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 py-8">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="border-4 border-sky-400 shadow-2xl">
            <CardContent className="p-8 text-center">
              <div className="w-24 h-24 bg-gradient-to-br from-sky-400 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-12 h-12 text-white" />
              </div>
              
              <h2 className="text-3xl font-black text-gray-800 mb-4">
                Ders Tamamlandı! 🎉
              </h2>
              
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-sky-100 rounded-2xl p-4">
                  <p className="text-3xl font-black text-sky-600">{score}/{lesson.exercises.length}</p>
                  <p className="text-sm text-gray-600">Doğru</p>
                </div>
                <div className="bg-indigo-100 rounded-2xl p-4">
                  <p className="text-3xl font-black text-indigo-600">{Math.round(finalScore)}%</p>
                  <p className="text-sm text-gray-600">Başarı</p>
                </div>
                <div className="bg-amber-100 rounded-2xl p-4">
                  <p className="text-3xl font-black text-amber-600">+{Math.round(finalScore / 10) * 10}</p>
                  <p className="text-sm text-gray-600"> Mil</p>
                </div>
              </div>
              
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => {
                    setPhase('learn');
                    setCurrentIndex(0);
                    setAnswers({});
                    setScore(0);
                    loadLesson();
                  }}
                  variant="outline"
                  className="border-2 border-sky-300"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Tekrar Yap
                </Button>
                <Button
                  onClick={() => navigate('/cabin-crew')}
                  className="bg-gradient-to-r from-sky-500 to-indigo-600"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Modüllere Dön
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Learning Phase
  const exercise = lesson?.exercises?.[currentIndex];
  const progress = ((currentIndex + 1) / (lesson?.exercises?.length || 1)) * 100;

  const renderExercise = () => {
    if (!exercise) return null;

    switch (exercise.type) {
      case 'vocabulary':
        return (
          <div className="text-center">
            <div 
              className="mb-6 cursor-pointer group"
              onClick={() => playAudio(exercise.content?.word)}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-sky-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Volume2 className="w-10 h-10 text-white" />
              </div>
              <p className="text-4xl font-black text-gray-800 mb-2">
                {exercise.content?.word}
              </p>
              {exercise.content?.pronunciation && (
                <p className="text-lg text-gray-500 italic">
                  {exercise.content?.pronunciation}
                </p>
              )}
            </div>
            
            <div className="bg-sky-50 rounded-xl p-4 mb-4">
              <p className="text-xl font-bold text-sky-700">
                {exercise.content?.translation}
              </p>
            </div>
            
            {exercise.content?.example && (
              <div className="bg-gray-50 rounded-xl p-4 mt-4">
                <p className="text-lg text-gray-700 italic mb-1">
                  &quot;{exercise.content.example}&quot;
                </p>
                {exercise.content?.example_translation && (
                  <p className="text-sm text-gray-500">
                    {exercise.content.example_translation}
                  </p>
                )}
              </div>
            )}
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-6 bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Öğrendim, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'dialogue':
        return (
          <div>
            <div className="bg-indigo-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-indigo-600 font-medium">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                {exercise.content?.context}
              </p>
            </div>
            
            <div className="space-y-4">
              {exercise.content?.lines?.map((line, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-xl ${
                    line.speaker === 'Crew' 
                      ? 'bg-sky-100 ml-8' 
                      : 'bg-gray-100 mr-8'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={line.speaker === 'Crew' ? 'bg-sky-500' : 'bg-gray-500'}>
                      {line.speaker}
                    </Badge>
                    <button 
                      onClick={() => playAudio(line.text)}
                      className="text-gray-400 hover:text-sky-500"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-lg font-medium text-gray-800">{line.text}</p>
                  <p className="text-sm text-gray-500">{line.translation}</p>
                </div>
              ))}
            </div>
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-6 w-full bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Diyaloğu Anladım
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'fill_blank':
        return (
          <FillBlankExerciseLesson 
            exercise={exercise}
            showResult={showResult}
            selectedOption={selectedOption}
            isCorrect={isCorrect}
            handleAnswer={handleAnswer}
          />
        );

      case 'scenario':
        return (
          <div>
            <div className="bg-amber-50 rounded-xl p-4 mb-4">
              <p className="text-lg font-bold text-gray-800">{exercise.content?.situation}</p>
              <p className="text-sm text-gray-600">{exercise.content?.situation_tr}</p>
            </div>
            
            <div className="space-y-3">
              {exercise.content?.options?.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(option.text, idx)}
                  disabled={showResult}
                  className={`w-full p-4 rounded-xl font-bold text-left transition-all border-3 ${
                    showResult && option.is_correct
                      ? 'bg-green-100 border-green-500 text-green-700'
                      : showResult && selectedOption === idx && !option.is_correct
                        ? 'bg-red-100 border-red-500 text-red-700'
                        : selectedOption === idx
                          ? 'bg-sky-100 border-sky-500'
                          : 'bg-white border-gray-200 hover:border-sky-400 hover:bg-sky-50'
                  }`}
                >
                  {option.text}
                </button>
              ))}
            </div>
          </div>
        );

      case 'matching': {
        // Handle different data structures from AI
        let matchTerms = [];
        let matchMeanings = [];
        
        const content = exercise.content || {};
        
        if (content.pairs) {
          // pairs can be array or object
          if (Array.isArray(content.pairs)) {
            matchTerms = content.pairs.map(p => p.english || p.term || '');
            matchMeanings = content.pairs.map(p => p.turkish || p.match || '');
          } else {
            // pairs is an object like { "Seat": "Koltuk", "Crew": "Mürettebat" }
            matchTerms = Object.keys(content.pairs);
            matchMeanings = Object.values(content.pairs);
          }
        } else if (content.terms) {
          matchTerms = content.terms;
          matchMeanings = content.translations || content.meanings || content.turkish || [];
        } else if (content.english) {
          matchTerms = content.english;
          matchMeanings = content.turkish || content.meanings || [];
        }
        
        return (
          <div>
            <p className="text-lg font-bold text-gray-800 mb-4 text-center">
              {exercise.instruction || "İngilizce terimleri Türkçe anlamlarıyla eşleştirin"}
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {/* English Terms */}
              <div className="space-y-3">
                <p className="text-sm font-bold text-sky-600 mb-2">English</p>
                {matchTerms.length > 0 ? matchTerms.map((term, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-sky-50 rounded-xl border-2 border-sky-200 font-medium text-gray-800"
                  >
                    {term}
                  </div>
                )) : <p className="text-gray-400">Yükleniyor...</p>}
              </div>
              
              {/* Turkish Meanings */}
              <div className="space-y-3">
                <p className="text-sm font-bold text-indigo-600 mb-2">Türkçe</p>
                {matchMeanings.length > 0 ? matchMeanings.map((meaning, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-indigo-50 rounded-xl border-2 border-indigo-200 font-medium text-gray-800"
                  >
                    {meaning}
                  </div>
                )) : <p className="text-gray-400">Yükleniyor...</p>}
              </div>
            </div>
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-6 w-full bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Eşleştirmeleri Gördüm, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
      }

      case 'pronunciation': {
        const pronunciationWord = exercise.content?.word || exercise.content?.text || '';
        const phonetic = exercise.content?.phonetic || exercise.content?.pronunciation || '';
        
        return (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Bu kelimeyi telaffuz edin:</p>
            
            <div 
              className="mb-6 cursor-pointer group"
              onClick={() => playAudio(pronunciationWord)}
            >
              <div className="w-24 h-24 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                <Volume2 className="w-12 h-12 text-white" />
              </div>
              <p className="text-3xl font-black text-gray-800 mb-2">
                {pronunciationWord}
              </p>
              {phonetic && (
                <p className="text-lg text-gray-500 italic">
                  {phonetic}
                </p>
              )}
            </div>
            
            {exercise.content?.translation && (
              <div className="bg-purple-50 rounded-xl p-4 mb-4">
                <p className="text-xl font-bold text-purple-700">
                  {exercise.content.translation}
                </p>
              </div>
            )}
            
            {exercise.content?.tips && exercise.content.tips.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4 mb-4 text-left">
                <p className="text-sm font-bold text-amber-700 mb-2">💡 Telaffuz İpuçları:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {exercise.content.tips.map((tip, idx) => (
                    <li key={idx}>• {tip}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-4 bg-gradient-to-r from-pink-500 to-purple-600"
            >
              Telaffuz Ettim, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
      }

      case 'icao_spelling':
        return (
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Bu kelimeyi ICAO alfabesiyle heceleyin:</p>
            <p className="text-4xl font-black text-gray-800 mb-6">
              {exercise.content?.word}
            </p>
            
            <div className="bg-sky-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-500 mb-2">Doğru heceleme:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {exercise.content?.correct_spelling?.map((word, idx) => (
                  <span key={idx} className="px-3 py-1 bg-sky-100 rounded-lg font-bold text-sky-700">
                    {word}
                  </span>
                )) || <span className="text-gray-500">Yükleniyor...</span>}
              </div>
            </div>
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="mt-4 bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Anladım, Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'pa_announcement': {
        const announcementText = exercise.content?.text_en || exercise.content?.text || '';
        const translationText = exercise.content?.text_tr || exercise.content?.translation || '';
        const tips = exercise.content?.tips || [];
        
        return (
          <div>
            <Badge className="bg-purple-500 text-white mb-4">
              pa_announcement
            </Badge>
            <p className="text-gray-600 mb-4 ml-2">Bu anonsu okuyun</p>
            
            <Badge className="bg-orange-500 text-white mb-4">
              {exercise.content?.announcement_type || 'Genel'} Anonsu
            </Badge>
            
            <div 
              className="bg-gray-900 text-white rounded-xl p-6 mb-4 cursor-pointer"
              onClick={() => playAudio(announcementText)}
            >
              <div className="flex items-center gap-2 mb-3">
                <Volume2 className="w-5 h-5 text-sky-400" />
                <span className="text-sky-400 text-sm">Dinlemek için tıklayın</span>
              </div>
              <p className="text-lg leading-relaxed">
                {announcementText || 'Metin yükleniyor...'}
              </p>
            </div>
            
            <div className="bg-sky-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-500 mb-1">Türkçe:</p>
              <p className="text-gray-700">{translationText || 'Çeviri yükleniyor...'}</p>
            </div>
            
            {tips.length > 0 && (
              <div className="bg-amber-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-bold text-amber-700 mb-2">💡 İpuçları:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {tips.map((tip, idx) => (
                    <li key={idx}>• {tip}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <Button
              onClick={() => handleAnswer('viewed')}
              className="w-full bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
      }

      default:
        return (
          <div className="text-center">
            <p className="text-gray-600 mb-4">{exercise.instruction}</p>
            <Button
              onClick={() => handleAnswer('viewed')}
              className="bg-gradient-to-r from-sky-500 to-indigo-600"
            >
              Devam Et
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={() => navigate('/cabin-crew')}
            variant="ghost"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Modüller
          </Button>
          
          <Badge className="bg-gradient-to-r from-sky-500 to-indigo-600 text-white text-lg px-4 py-2">
            {lesson?.module?.name || moduleId}
          </Badge>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold text-gray-600">İlerleme</span>
            <span className="font-bold text-sky-600">{currentIndex + 1}/{lesson?.exercises?.length || 0}</span>
          </div>
          <Progress value={progress} className="h-4 bg-sky-200" />
        </div>

        {/* Exercise Card */}
        <Card className="border-4 border-sky-300 shadow-2xl">
          <CardContent className="p-8">
            {exercise && (
              <>
                <div className="flex items-center gap-2 mb-6">
                  <Badge className="bg-sky-500 text-white">
                    {exercise.type?.replace('_', ' ')}
                  </Badge>
                  {exercise.instruction && (
                    <p className="text-sm text-gray-500">{exercise.instruction}</p>
                  )}
                </div>
                
                {renderExercise()}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CabinCrewLesson;
